import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { User, Mail, Phone, Calendar, ShieldCheck, Sparkles, Crown, Wallet, Clock, CheckCircle2, ShieldAlert, Award } from "lucide-react";
import { fmtDateTime12 } from "@/lib/time";

export default function CustomerProfile() {
  const { user } = useAuth();
  const [walletInfo, setWalletInfo] = useState(null);

  useEffect(() => {
    api.get("/wallet/mine")
      .then((res) => setWalletInfo(res.data))
      .catch(() => setWalletInfo(null));
  }, []);

  const purchaseTx = walletInfo?.transactions?.find((t) => t.type === "purchase" || t.type === "adjust_add");

  return (
    <div data-testid="customer-profile" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
          <ShieldCheck size={16} /> PERSONAL INFORMATION · VIP GUEST
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
          Account Profile
        </h1>
      </motion.div>

      {/* Main Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl rounded-2xl border border-amber-400/70 p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-2xl shadow-yellow-950/20 space-y-6"
      >
        <div className="flex items-center gap-4 pb-6 border-b border-white/10">
          <div className="w-16 h-16 rounded-2xl border border-amber-400/80 bg-gradient-to-r from-amber-500/20 to-amber-300/10 flex items-center justify-center font-heading text-2xl font-bold text-amber-300 shadow-md">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">{user?.name}</h2>
            <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
              <Sparkles size={13} className="text-amber-400" /> Hammam Registered Client
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
              <User size={12} /> Full Name
            </div>
            <div className="text-base font-bold text-white">{user?.name}</div>
          </div>

          <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
              <Mail size={12} /> Email Address
            </div>
            <div className="text-base font-bold text-white truncate">{user?.email}</div>
          </div>

          <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
              <Phone size={12} /> Mobile Number
            </div>
            <div className="text-base font-bold text-white">{user?.phone || "—"}</div>
          </div>

          <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
              <Calendar size={12} /> Member Since
            </div>
            <div className="text-base font-bold text-white">{fmtDateTime12(user?.created_at) || (user?.created_at || "").slice(0, 10)}</div>
          </div>
        </div>
      </motion.div>

      {/* Active Membership Status Section */}
      {walletInfo?.membership_name ? (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="max-w-2xl rounded-2xl border border-amber-400/80 p-8 bg-gradient-to-b from-amber-950/40 via-black/95 to-hpaper shadow-2xl shadow-yellow-950/20 space-y-6"
        >
          <div className="flex items-center justify-between pb-4 border-b border-white/10">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold flex items-center gap-2">
              <Crown size={18} className="text-amber-400" /> ACTIVE MEMBERSHIP PRIVILEGES
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-extrabold uppercase bg-emerald-950/80 text-emerald-300 border border-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 size={13} /> Active Tier
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl border border-amber-400/30 bg-amber-950/20 space-y-1">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/50">Membership Tier</div>
              <div className="text-xl font-heading font-extrabold text-amber-300 flex items-center gap-2">
                <Award size={18} /> {walletInfo.membership_name}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-emerald-400/30 bg-emerald-950/20 space-y-1">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/50">Available Wallet Balance</div>
              <div className="text-xl font-heading font-extrabold text-emerald-400 flex items-center gap-2">
                <Wallet size={18} /> {inr(walletInfo.balance || 0)}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                <Calendar size={12} className="text-amber-400" /> Purchased / Activated On
              </div>
              <div className="text-sm font-bold text-white">
                {purchaseTx ? fmtDateTime12(purchaseTx.created_at) : (walletInfo.updated_at ? fmtDateTime12(walletInfo.updated_at) : "Active")}
              </div>
            </div>

            <div className="p-4 rounded-xl border border-white/10 bg-black/50 space-y-1">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                <Clock size={12} className="text-emerald-400" /> Membership Valid Until
              </div>
              <div className="text-sm font-bold text-emerald-300">
                {walletInfo.expires_at ? (walletInfo.expires_at.slice(0, 10)) : "1 Year Validity"}
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="max-w-2xl rounded-2xl border border-white/15 p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl space-y-5"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 font-extrabold">
            <ShieldAlert size={18} className="text-amber-400" /> MEMBERSHIP STATUS
          </div>

          <div className="p-5 rounded-xl border border-amber-400/40 bg-amber-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-1">No Active Membership Plan</h3>
              <p className="text-xs text-white/70 leading-relaxed max-w-md font-medium">
                You currently do not have an active Hammam Membership. Purchase a plan to receive up to ₹5,000 extra bonus wallet credits & exclusive VIP perks across all branches.
              </p>
            </div>
            <Link
              to="/membership"
              className="gold-btn py-3 px-5 text-xs font-extrabold uppercase tracking-wider rounded-xl shrink-0 flex items-center gap-2 shadow-lg"
            >
              <Sparkles size={14} /> Explore Plans
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}
